import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import photo from './headshot.png';

import {
  SiPython,
  SiCplusplus,
  SiJavascript,
  SiCss3,
  SiNodedotjs,
  SiReact,
  SiNumpy,
  SiJupyter,
  SiJira,
} from 'react-icons/si';
import { FaJava, FaMicrosoft } from 'react-icons/fa';
import { MdShowChart } from 'react-icons/md';
import { ArrowLeft } from 'lucide-react';

const SKILLS = [
  { name: 'Python', icon: <SiPython size={30} color="#306998" /> },
  { name: 'Java', icon: <FaJava size={30} color="#007396" /> },
  { name: 'C/C++', icon: <SiCplusplus size={30} color="#00599C" /> },
  { name: 'Javascript', icon: <SiJavascript size={30} color="#F7DF1E" /> },
  { name: 'CSS', icon: <SiCss3 size={30} color="#1572B6" /> },
  { name: 'Node.js', icon: <SiNodedotjs size={30} color="#339933" /> },
  { name: 'React', icon: <SiReact size={30} color="#61DAFB" /> },
  { name: 'Web Hosting', icon: <SiNodedotjs size={30} color="#339933" /> },
  { name: 'NumPy', icon: <SiNumpy size={30} color="#013243" /> },
  { name: 'Matplotlib', icon: <MdShowChart size={30} color="#11557C" /> },
  { name: 'Jupyter', icon: <SiJupyter size={30} color="#F37626" /> },
  { name: 'Jira', icon: <SiJira size={30} color="#0052CC" /> },
  { name: 'Microsoft Office', icon: <FaMicrosoft size={30} color="#F25022" /> },
];

const SKILL_DESCRIPTIONS = {
  Python: 'High-level language widely used in software development and data science.',
  Java: 'Versatile, object-oriented language used for web, mobile, and enterprise apps.',
  'C/C++': 'Powerful languages for systems programming, game dev, and high performance.',
  Javascript: 'Scripting language used to create interactive effects in web browsers.',
  CSS: 'Style sheet language for describing presentation of documents in HTML.',
  'Node.js': "JavaScript runtime on Chrome's V8 engine for scalable applications.",
  React: 'Libraries for building user interfaces, web apps, and mobile apps.',
  'Web Hosting': 'Managing storage and delivery of website content online.',
  NumPy: 'Python library used for scientific computing and n-dimensional arrays.',
  Matplotlib: 'Plotting library for Python producing publication-quality figures.',
  Jupyter: 'Open-source app to create and share documents with live code.',
  Jira: 'Popular tool for agile project management and tracking issues.',
  'Microsoft Office': 'Suite of office applications for productivity and management.',
};

const HONEYCOMB_LARGE = [4, 5, 4];
const HONEYCOMB_SMALL = [3, 2, 3, 2, 3];

function chunkSkills(skills, layout) {
  const rows = [];
  let i = 0;

  layout.forEach((count) => {
    rows.push(skills.slice(i, i + count));
    i += count;
  });

  return rows;
}

function StaticSkillsFallback({ circleSize }) {
  return (
    <div
      className="font-montserrat mt-[20px] flex flex-col lg:flex-row gap-16 bg-offwhite justify-center items-center"
      style={{ minHeight: circleSize, overflowAnchor: 'none' }}
    >
      <div className="flex flex-col items-center">
        <h2 className="mb-6 text-lg lg:text-base font-semibold">My Skills</h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-[520px]">
          {SKILLS.map(({ name, icon }) => (
            <div
              key={name}
              className="rounded-2xl border border-sage bg-offwhite px-4 py-3 flex items-center gap-3"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
            >
              <div>{icon}</div>
              <div className="text-sm">{name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="hidden lg:flex flex-col items-center justify-center">
        <img
          src={photo}
          alt="headshot"
          className="rounded-full"
          style={{
            width: circleSize,
            height: circleSize,
            boxShadow: '0 8px 20px rgba(0,0,0,0.14)',
          }}
          loading="lazy"
          decoding="async"
          draggable="false"
        />
      </div>
    </div>
  );
}

function OrbitView({
  circleSize,
  bubbleDiameter,
  selectedSkill,
  setSelectedSkill,
  setAltView,
  orbitPositions,
}) {
  const center = circleSize / 2;

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: circleSize,
        height: circleSize,
        contain: 'layout paint',
      }}
    >
      <div
        className="absolute rounded-full bg-offwhite border-2 border-sage flex flex-col items-center justify-center text-center p-6"
        style={{
          width: bubbleDiameter,
          height: bubbleDiameter,
          left: center - bubbleDiameter / 2,
          top: center - bubbleDiameter / 2,
          zIndex: 10,
          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
        }}
      >
        {selectedSkill ? (
          <>
            <div className="text-6xl lg:text-5xl mb-4">
              {SKILLS.find((s) => s.name === selectedSkill)?.icon}
            </div>

            <h3 className="font-bold text-xl lg:text-lg mb-2">{selectedSkill}</h3>

            <p className="text-[13px] lg:text-[15px] text-gray-700 leading-relaxed px-4 mb-4">
              {SKILL_DESCRIPTIONS[selectedSkill]}
            </p>

            <button
              onClick={() => setSelectedSkill(null)}
              className="flex items-center gap-1 text-sm lg:text-xs text-gray-600 hover:text-gray-800"
              type="button"
            >
              <ArrowLeft size={16} /> Back
            </button>
          </>
        ) : (
          <>
            <p className="text-gray-500 mb-3 text-[15px] lg:text-[15px]">
              Click a skill to learn more!
            </p>

            <button
              onClick={() => setAltView(true)}
              className="mt-2 px-3 py-1 rounded border border-sage text-sage text-[15px] lg:text-[15px] hover:bg-sage/15 transition-colors"
              type="button"
            >
              Try Alternative View
            </button>
          </>
        )}
      </div>

      <div className="absolute inset-0">
        {SKILLS.map(({ name, icon }, i) => {
          const pos = orbitPositions[i];

          return (
            <button
              key={name}
              className="absolute flex items-center justify-center rounded-full bg-offwhite cursor-pointer transition-transform duration-150 hover:scale-105"
              style={{
                left: pos.x,
                top: pos.y,
                width: pos.iconSize,
                height: pos.iconSize,
                boxShadow: '0 4px 10px rgba(0,0,0,0.10)',
              }}
              onClick={() => setSelectedSkill(name)}
              type="button"
            >
              {icon}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HoneycombView({
  selectedSkill,
  setSelectedSkill,
  setAltView,
  smallRows,
  largeRows,
}) {
  return (
    <div className="flex flex-col items-center">
      <h2 className="mb-6 text-lg lg:text-base font-semibold">My Skills</h2>

      <div className="block lg:hidden">
        <div className="honeycomb-grid">
          {smallRows.map((row, rowIndex) => (
            <div key={rowIndex} className="honeycomb-row">
              {row.map(({ name, icon }) => (
                <button
                  key={name}
                  className="honeycomb-button"
                  onClick={() => setSelectedSkill(name)}
                  type="button"
                >
                  <div className="honeycomb-shape">
                    <div className="honeycomb-inner">
                      <div className="text-2xl lg:text-xl mb-1">{icon}</div>
                      <p className="text-xs lg:text-[0.65rem]">{name}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="honeycomb-grid">
          {largeRows.map((row, rowIndex) => (
            <div key={rowIndex} className="honeycomb-row">
              {row.map(({ name, icon }) => (
                <button
                  key={name}
                  className="honeycomb-button"
                  onClick={() => setSelectedSkill(name)}
                  type="button"
                >
                  <div className="honeycomb-shape">
                    <div className="honeycomb-inner">
                      <div className="text-2xl lg:text-xl mb-1">{icon}</div>
                      <p className="text-xs lg:text-[0.65rem]">{name}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {selectedSkill && (
        <div
          className="mt-10 w-[90%] max-w-xl rounded-2xl border border-sage bg-offwhite p-6 text-center"
          style={{ boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}
        >
          <div className="text-5xl mb-4">
            {SKILLS.find((s) => s.name === selectedSkill)?.icon}
          </div>

          <h3 className="font-bold text-xl mb-3">{selectedSkill}</h3>

          <p className="text-base text-gray-700 leading-relaxed px-4">
            {SKILL_DESCRIPTIONS[selectedSkill]}
          </p>
        </div>
      )}

      <button
        onClick={() => setAltView(false)}
        className="mt-[50px] px-3 py-1 rounded border border-black text-black text-sm lg:text-xs hover:bg-gray-200 transition-colors"
        type="button"
      >
        Back to Orbit View
      </button>
    </div>
  );
}

function ProfessionalSkills() {
  const [isLargeScreen, setIsLargeScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [altView, setAltView] = useState(false);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const circleSize = isLargeScreen ? 450 : 420;
  const bubbleDiameter = isLargeScreen ? 275 : 240;
  const orbitRadius = circleSize / 2 - 40;
  const iconSize = 54;
  const iconR = iconSize / 2;
  const center = circleSize / 2;

  const smallRows = useMemo(() => chunkSkills(SKILLS, HONEYCOMB_SMALL), []);
  const largeRows = useMemo(() => chunkSkills(SKILLS, HONEYCOMB_LARGE), []);

  const orbitPositions = useMemo(() => {
    return SKILLS.map((_, i) => {
      const angle = (i / SKILLS.length) * 2 * Math.PI - Math.PI / 2 + rotation;

      return {
        x: center + orbitRadius * Math.cos(angle) - iconR,
        y: center + orbitRadius * Math.sin(angle) - iconR,
        iconSize,
      };
    });
  }, [center, orbitRadius, iconR, iconSize, rotation]);

  useEffect(() => {
    const img = new Image();
    img.src = photo;
    img.decoding = 'async';

    let idleId;
    let timeoutId;

    const enableInteractive = () => setInteractiveReady(true);

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(enableInteractive, { timeout: 1000 });
    } else {
      timeoutId = window.setTimeout(enableInteractive, 250);
    }

    return () => {
      if (idleId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
  if (!interactiveReady || altView) return undefined;

  let animationFrameId;

  const animate = () => {
    setRotation((prev) => prev + 0.0025);
    animationFrameId = window.requestAnimationFrame(animate);
  };

  animationFrameId = window.requestAnimationFrame(animate);

  return () => {
    window.cancelAnimationFrame(animationFrameId);
  };
}, [interactiveReady, altView]);

  if (!interactiveReady) {
    return <StaticSkillsFallback circleSize={circleSize} />;
  }

  return (
    <div
      className="font-montserrat mt-[20px] flex flex-col lg:flex-row gap-16 bg-offwhite justify-center items-center"
      style={{ minHeight: circleSize, overflowAnchor: 'none' }}
    >
      {!altView ? (
        <OrbitView
          circleSize={circleSize}
          bubbleDiameter={bubbleDiameter}
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          setAltView={setAltView}
          orbitPositions={orbitPositions}
        />
      ) : (
        <HoneycombView
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          setAltView={setAltView}
          smallRows={smallRows}
          largeRows={largeRows}
        />
      )}

      <div className="hidden lg:flex flex-col items-center justify-center">
        <img
          src={photo}
          alt="headshot"
          className="rounded-full"
          style={{
            width: circleSize,
            height: circleSize,
            boxShadow: '0 8px 20px rgba(0,0,0,0.14)',
          }}
          loading="lazy"
          decoding="async"
          draggable="false"
        />
      </div>
    </div>
  );
}

export default ProfessionalSkills;